import { useState, useEffect, useMemo, useRef, type FC } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import Fade from '@mui/material/Fade';
import Skeleton from '@mui/material/Skeleton';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import {
  Storage as InfrastructureIcon,
  AccountTree as EksIcon,
  Dataset as DatabaseIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { InfrastructureSelectionCardProps } from './types';
import { useInfrastructureData } from '../../hooks/useInfrastructureData';
import { useRepositoryData } from '../../hooks/useRepositoryData';

const StyledCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(2),
  background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.secondary.light}10 50%, ${theme.palette.background.paper} 100%)`,
  border: `1px solid ${theme.palette.primary.light}40`,
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-2px)',
    background: `linear-gradient(135deg, ${theme.palette.primary.light}20 0%, ${theme.palette.secondary.light}15 50%, ${theme.palette.background.paper} 100%)`,
  },
}));

const CardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(3),
  paddingBottom: theme.spacing(2),
  borderBottom: `2px solid ${theme.palette.primary.main}`,
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: 400,
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
  '&::-webkit-scrollbar': {
    width: 8,
  },
  '&::-webkit-scrollbar-track': {
    background: theme.palette.action.hover,
    borderRadius: 4,
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.primary.main,
    borderRadius: 4,
    '&:hover': {
      background: theme.palette.primary.dark,
    },
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    transform: 'scale(1.002)',
  },
  '&.Mui-selected': {
    backgroundColor: `${theme.palette.primary.light}20`,
    '&:hover': {
      backgroundColor: `${theme.palette.primary.light}30`,
    },
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1.5),
  fontSize: '0.9rem',
  fontWeight: 500,
}));

const StyledTableHeaderCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(2, 1.5),
  fontSize: '0.85rem',
  fontWeight: 700,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  position: 'sticky',
  top: 0,
  zIndex: 3,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const TypeChip = styled(Chip)(({ theme }) => ({
  fontSize: '0.75rem',
  height: 24,
  fontWeight: 600,
  '&.eks': {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.contrastText,
  },
  '&.rds-instance': {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
  },
  '&.default': {
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.text.primary,
  },
}));

const EmptyStateBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  color: theme.palette.text.secondary,
  textAlign: 'center',
}));

const getTypeIcon = (type: string) => {
  switch (type.toLowerCase()) {
    case 'eks':
      return <EksIcon />;
    case 'rds-instance':
    case 'database':
      return <DatabaseIcon />;
    default:
      return <InfrastructureIcon />;
  }
};

const getTypeChipClass = (type: string): string => {
  switch (type.toLowerCase()) {
    case 'eks':
      return 'eks';
    case 'rds-instance':
      return 'rds-instance';
    default:
      return 'default';
  }
};

export const InfrastructureSelectionCard: FC<
  InfrastructureSelectionCardProps
> = ({
  selectedRepository,
  selectedInfrastructures = [],
  onInfrastructureChange,
}) => {
  const { repositories } = useRepositoryData();
  const [localSelectedInfrastructures, setLocalSelectedInfrastructures] =
    useState<string[]>(selectedInfrastructures);
  const [lastRepositorySystem, setLastRepositorySystem] = useState<
    string | undefined
  >();

  // Track which repository system we've already auto-preselected for so we
  // don't re-run preselection after the user explicitly clears all selections.
  const autoPreselectedForSystemRef = useRef<string | undefined>(undefined);

  // Get the selected repository's system
  const selectedRepo = repositories.find(
    repo => repo.value === selectedRepository,
  );
  const selectedRepositorySystem = selectedRepo?.system;

  const { infrastructures, loading, error } = useInfrastructureData(
    selectedRepositorySystem,
  );

  // Display ordering: preselected types (eks, rds-instance) first, then sort by type and name
  const displayedInfrastructures = useMemo(() => {
    const preTypes = ['eks', 'account'];
    return [...infrastructures].sort((a, b) => {
      const aPre = preTypes.includes(a.type);
      const bPre = preTypes.includes(b.type);
      if (aPre && !bPre) return -1;
      if (!aPre && bPre) return 1;
      const typeCmp = a.type.localeCompare(b.type);
      if (typeCmp !== 0) return typeCmp;
      return a.name.localeCompare(b.name);
    });
  }, [infrastructures]);

  // Sync with parent props when they change
  useEffect(() => {
    setLocalSelectedInfrastructures(selectedInfrastructures);
  }, [selectedInfrastructures]);

  // Handle repository system changes (clear selections and auto-preselect)
  useEffect(() => {
    if (selectedRepositorySystem !== lastRepositorySystem) {
      setLastRepositorySystem(selectedRepositorySystem);

      if (!selectedRepositorySystem) {
        // Repository cleared - clear selections
        setLocalSelectedInfrastructures([]);
        onInfrastructureChange?.([]);
        // Reset auto-preselection tracker when repository is cleared
        autoPreselectedForSystemRef.current = undefined;
        return;
      }

      // Repository changed - clear selections first
      setLocalSelectedInfrastructures([]);
      onInfrastructureChange?.([]);
      // Reset auto-preselection tracker for the new repository system so
      // auto-preselection can run once when infrastructures load.
      autoPreselectedForSystemRef.current = undefined;
    }
  }, [selectedRepositorySystem, lastRepositorySystem, onInfrastructureChange]);

  // Auto-preselect eks and rds-instance types when infrastructure data loads
  useEffect(() => {
    // Only auto-preselect once per repository system when infrastructures
    // first load. If the user clears all selections later, do not re-auto-
    // preselect.
    if (
      infrastructures.length > 0 &&
      localSelectedInfrastructures.length === 0 &&
      selectedRepositorySystem === lastRepositorySystem &&
      autoPreselectedForSystemRef.current !== selectedRepositorySystem
    ) {
      const preselectedInfrastructures = infrastructures
        .filter(infra => infra.type === 'eks' || infra.type === 'account')
        .map(infra => infra.value);

      if (preselectedInfrastructures.length > 0) {
        setLocalSelectedInfrastructures(preselectedInfrastructures);
        onInfrastructureChange?.(preselectedInfrastructures);
        // Mark that we've auto-preselected for this repository system so we
        // don't do it again on subsequent clears.
        autoPreselectedForSystemRef.current = selectedRepositorySystem;
      }
    }
  }, [
    infrastructures,
    localSelectedInfrastructures.length,
    selectedRepositorySystem,
    lastRepositorySystem,
    onInfrastructureChange,
  ]);

  const handleInfrastructureToggle = (infrastructureValue: string) => {
    const newSelected = localSelectedInfrastructures.includes(
      infrastructureValue,
    )
      ? localSelectedInfrastructures.filter(
          value => value !== infrastructureValue,
        )
      : [...localSelectedInfrastructures, infrastructureValue];

    setLocalSelectedInfrastructures(newSelected);
    onInfrastructureChange?.(newSelected);
  };

  const handleSelectAll = () => {
    const allValues = displayedInfrastructures.map(infra => infra.value);
    const displayedSelectedCount = displayedInfrastructures.filter(infra =>
      localSelectedInfrastructures.includes(infra.value),
    ).length;
    const newSelected =
      displayedSelectedCount === displayedInfrastructures.length
        ? // unselect displayed
          localSelectedInfrastructures.filter(v => !allValues.includes(v))
        : // add displayed
          Array.from(new Set([...localSelectedInfrastructures, ...allValues]));

    setLocalSelectedInfrastructures(newSelected);
    onInfrastructureChange?.(newSelected);
  };

  // Don't render if no repository is selected
  if (!selectedRepository) {
    return null;
  }

  return (
    <Fade in timeout={300}>
      <StyledCard>
        <CardContent>
          <CardHeader>
            <InfrastructureIcon color="primary" />
            <Typography variant="h6" color="primary" fontWeight="bold">
              Infrastructure Dependencies
            </Typography>
          </CardHeader>

          {loading && (
            <Box>
              <Skeleton
                variant="rectangular"
                height={50}
                sx={{ mb: 1, borderRadius: 1 }}
              />
              <Skeleton
                variant="rectangular"
                height={50}
                sx={{ mb: 1, borderRadius: 1 }}
              />
              <Skeleton
                variant="rectangular"
                height={50}
                sx={{ mb: 1, borderRadius: 1 }}
              />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load infrastructure dependencies: {error}
            </Alert>
          )}

          {!loading && !error && (
            <StyledTableContainer>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <StyledTableHeaderCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          localSelectedInfrastructures.length > 0 &&
                          localSelectedInfrastructures.length <
                            displayedInfrastructures.length
                        }
                        checked={
                          displayedInfrastructures.length > 0 &&
                          displayedInfrastructures.filter(infra =>
                            localSelectedInfrastructures.includes(infra.value),
                          ).length === displayedInfrastructures.length
                        }
                        onChange={handleSelectAll}
                        disabled={displayedInfrastructures.length === 0}
                      />
                    </StyledTableHeaderCell>
                    <StyledTableHeaderCell>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography variant="inherit">Name</Typography>
                        {localSelectedInfrastructures.length > 0 && (
                          <Chip
                            label={`${localSelectedInfrastructures.length} selected`}
                            size="small"
                            color="primary"
                            sx={{ ml: 1, fontSize: '0.7rem', height: 20 }}
                          />
                        )}
                      </Box>
                    </StyledTableHeaderCell>
                    <StyledTableHeaderCell>Type</StyledTableHeaderCell>
                    <StyledTableHeaderCell>Account</StyledTableHeaderCell>
                    <StyledTableHeaderCell>Lifecycle</StyledTableHeaderCell>
                    <StyledTableHeaderCell>Region</StyledTableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedInfrastructures.length === 0 ? (
                    <TableRow>
                      <StyledTableCell colSpan={6}>
                        <EmptyStateBox>
                          <InfrastructureIcon
                            sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }}
                          />
                          <Typography variant="body1" color="text.secondary">
                            No infrastructure
                          </Typography>
                          <Typography variant="body2" color="text.disabled">
                            No infrastructure entities found for the selected
                            repository's system.
                          </Typography>
                        </EmptyStateBox>
                      </StyledTableCell>
                    </TableRow>
                  ) : (
                    displayedInfrastructures.map(infrastructure => {
                      const isSelected = localSelectedInfrastructures.includes(
                        infrastructure.value,
                      );
                      return (
                        <StyledTableRow
                          key={infrastructure.value}
                          selected={isSelected}
                          onClick={() =>
                            handleInfrastructureToggle(infrastructure.value)
                          }
                          style={{ cursor: 'pointer' }}
                        >
                          <StyledTableCell padding="checkbox">
                            <Checkbox
                              checked={isSelected}
                              onChange={e => {
                                e.stopPropagation();
                                handleInfrastructureToggle(
                                  infrastructure.value,
                                );
                              }}
                            />
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              {getTypeIcon(infrastructure.type)}
                              <Typography variant="body2" fontWeight="medium">
                                {infrastructure.name}
                              </Typography>
                            </Box>
                          </StyledTableCell>
                          <StyledTableCell>
                            <TypeChip
                              label={infrastructure.type}
                              size="small"
                              className={getTypeChipClass(infrastructure.type)}
                            />
                          </StyledTableCell>
                          <StyledTableCell>
                            <Typography variant="body2" color="text.secondary">
                              {infrastructure.account}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Typography variant="body2" color="text.secondary">
                              {infrastructure.lifecycle}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell>
                            <Typography variant="body2" color="text.secondary">
                              {infrastructure.region}
                            </Typography>
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </StyledTableContainer>
          )}

          {!loading && !error && displayedInfrastructures.length > 0 && (
            <Box mt={2}>
              <Typography variant="caption" color="text.secondary">
                {
                  localSelectedInfrastructures.filter(v =>
                    displayedInfrastructures.some(d => d.value === v),
                  ).length
                }{' '}
                of {displayedInfrastructures.length} selected
              </Typography>
            </Box>
          )}
        </CardContent>
      </StyledCard>
    </Fade>
  );
};
